import { computed, ref, type Ref } from 'vue'
import { SECTIONS } from '@/js/services/cnstrc/constants'
import { mergeSuggestedTerms } from '@/js/services/cnstrc/util'
import type { IRogue } from '@/js/services/RogueTypes'

// Which Constructor surface produced a term. The column mixes the two, and a term's source
// decides its tracking attributes: related searches come from the search response, everything
// else from autocomplete or a recommendations pod and keeps the attributes it has always had.
export type PhraseSource = 'related' | 'suggestion'

export interface Phrase {
  text: string
  url?: string
  html?: string
  source: PhraseSource
}

export interface RecommendationsMeta {
  podId: string
  resultId: string | undefined
  numResults: number
}

/** Terms for one query, as the autocomplete and recommendations responses supply them. */
export interface SuggestionTerms {
  query: string
  keyphrases: Phrase[]
  categories: Phrase[]
  popular: Phrase[]
  /** Set only when the popular list is the column itself, not a fallback under a query. */
  popularMeta: RecommendationsMeta | null
}

// How many terms each heading shows. Mobile narrows Did You Mean further, in CSS.
export const DID_YOU_MEAN_COUNT = 4
export const SHOP_BY_COUNT = 6

export function podContainerAttrs(meta: RecommendationsMeta | null) {
  if (!meta) return {}

  return {
    'data-cnstrc-recommendations'        : '',
    'data-cnstrc-recommendations-pod-id' : meta.podId,
    'data-cnstrc-result-id'              : meta.resultId,
    'data-cnstrc-num-results'            : meta.numResults,
  }
}

const emptyTerms = (): SuggestionTerms => ({ query: '', keyphrases: [], categories: [], popular: [], popularMeta: null })

/**
 * Owns the suggested-terms column of the search dropdown: which terms it shows under which
 * heading, and the Constructor attributes that attribute a click to the API it came from.
 *
 * Terms arrive from two responses that resolve independently — autocomplete and the search
 * preflight — so each setter rebuilds the column from whatever has answered so far.
 */
export function useSuggestionColumn(rogue: IRogue, query: Ref<string>) {
  const suggestions = ref<{ title: string, phrases: Phrase[] }>({ title: '', phrases: [] })
  // The term under the cursor. The input mirrors it while hovering, which changes the query
  // out from under us — see refresh.
  const hoveredTerm = ref<string | null>(null)

  const terms = ref<SuggestionTerms>(emptyTerms())
  const related = ref<{ term: string, phrases: Phrase[], resultId?: string } | null>(null)

  // The column becomes a related-searches container as soon as one related term is in it, so
  // the beacon has the result id and term those clicks belong to. Autocomplete-powered terms
  // alongside them keep their own attributes — see phraseAttrs.
  const containerAttrs = computed(() => {
    const meta = related.value
    if (!meta || !suggestions.value.phrases.some(phrase => phrase.source === 'related')) {
      return podContainerAttrs(terms.value.popularMeta)
    }

    return {
      'data-cnstrc-related-searches' : '',
      'data-cnstrc-result-id'        : meta.resultId,
      'data-cnstrc-search-term'      : meta.term,
    }
  })

  function phraseAttrs(phrase: Phrase) {
    if (phrase.source === 'related') {
      return { 'data-cnstrc-related-search-term': phrase.text }
    }

    return {
      'data-cnstrc-item'         : terms.value.popularMeta ? 'recommendation' : undefined,
      'data-cnstrc-item-section' : SECTIONS.SEARCH_SUGGESTIONS,
      'data-cnstrc-item-name'    : phrase.text,
      'data-cnstrc-item-id'      : phrase.text,
    }
  }

  function setTerms(next: SuggestionTerms) {
    terms.value = next
    refresh()
  }

  function setRelatedSearches(term: string, phrases: Phrase[], resultId?: string) {
    related.value = { term, phrases, resultId }
    refresh()
  }

  function clearRelatedSearches() {
    related.value = null
    refresh()
  }

  /** Rebuilds the column for the current query from every source that has answered for it. */
  function refresh() {
    const current = query.value
    // While a term is hovered the input mirrors it, so the query changes under us. Leave the
    // column alone rather than rebuilding it around the term being hovered.
    if (hoveredTerm.value && hoveredTerm.value === current) return

    // A term equal to the query suggests nothing and would spend one of the few slots there are
    const normalized = current.trim().toLowerCase()
    const usable = (phrases: Phrase[], answeredFor: string) => answeredFor !== current
      ? []
      : phrases.filter(phrase => phrase?.text && phrase.text.trim().toLowerCase() !== normalized)

    // Autocomplete's suggestions lead; related searches top the list up to the display cap
    const didYouMean = mergeSuggestedTerms(
      usable(terms.value.keyphrases, terms.value.query),
      usable(related.value?.phrases ?? [], related.value?.term ?? ''),
      SHOP_BY_COUNT
    )

    suggestions.value = didYouMean.length && current
      ? column(rogue.__('Did You Mean?'), didYouMean.slice(0, DID_YOU_MEAN_COUNT), current)
      : column(rogue.__('Shop By'), shopBy(didYouMean), current)
  }

  /**
   * Shop By is the standing fallback: the query's own categories, else the popular-searches
   * pod, else terms left over from a query that has since been cleared.
   */
  function shopBy(didYouMean: Phrase[]): Phrase[] {
    const categories = terms.value.query === query.value ? terms.value.categories : []
    if (categories.length) return categories.slice(0, SHOP_BY_COUNT)
    if (terms.value.popular.length) return terms.value.popular

    return didYouMean.slice(0, SHOP_BY_COUNT)
  }

  // Each term is rendered with the typed query bolded inside it
  function column(title: string, phrases: Phrase[], current: string) {
    return { title, phrases: phrases.map(phrase => ({ ...phrase, html: phrase.text.replace(current, `<b>${current}</b>`) })) }
  }

  return { suggestions, hoveredTerm, containerAttrs, phraseAttrs, setTerms, setRelatedSearches, clearRelatedSearches }
}
